import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  Crown, Check, Star, Sparkles, Coins, 
  Clock, FileText, Gift, Plus, MessageCircle,
  Lock, Unlock, Heart, Zap, Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import PurchaseDialog from '../components/PurchaseDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Packages() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [purchasing, setPurchasing] = useState(null);
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const [loyaltyTier, setLoyaltyTier] = useState('bronze');

  // Fetch credit balance and rewards progress
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setCreditBalance(res.data?.balance || 0))
        .catch(() => {});
      
      // Fetch user stats for rewards
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          setTotalSpent(res.data?.total_spent || 0);
          setLoyaltyTier(res.data?.loyalty_tier || 'bronze');
        })
        .catch(() => {});
    }
  }, [user]);

  // Gold Chat unlock threshold
  const GOLD_UNLOCK_THRESHOLD = 30;
  const goldProgress = Math.min((totalSpent / GOLD_UNLOCK_THRESHOLD) * 100, 100);
  const isGoldUnlocked = totalSpent >= GOLD_UNLOCK_THRESHOLD || loyaltyTier === 'gold' || loyaltyTier === 'diamond';

  // All prices are now in TOKENS
  const subscriptions = [
    { 
      id: 'daily_tease', 
      nameKey: 'memberships.dailyTease.name',
      name: 'Daily Tease', 
      tokens: 6,
      icon: '☕',
      descriptionKey: 'packages.subscriptions.dailyTease.desc',
      featuresKey: 'memberships.dailyTease.features'
    },
    { 
      id: 'midnight_crave', 
      nameKey: 'memberships.midnightCrave.name',
      name: 'Midnight Crave', 
      tokens: 10,
      icon: '🍺',
      descriptionKey: 'packages.subscriptions.midnightCrave.desc',
      featuresKey: 'memberships.midnightCrave.features'
    },
    { 
      id: 'pure_obsession', 
      nameKey: 'memberships.pureObsession.name',
      name: 'Pure Obsession', 
      tokens: 15,
      icon: '🎬',
      popular: true,
      descriptionKey: 'packages.subscriptions.pureObsession.desc',
      featuresKey: 'memberships.pureObsession.features'
    },
    { 
      id: 'gold_vip', 
      nameKey: 'memberships.goldVip.name',
      name: 'Gold VIP', 
      tokens: 30,
      icon: '👑',
      descriptionKey: 'packages.subscriptions.goldVip.desc',
      featuresKey: 'memberships.goldVip.features'
    }
  ];

  const oneTimePackages = [
    { 
      id: 'starter_pack', 
      name: 'Starter Pack', 
      tokens: 20,
      items: '10 Sets',
      descriptionKey: 'packages.oneTime.starter.desc',
      featuresKey: 'packages.oneTime.starter.features'
    },
    { 
      id: 'premium_collection', 
      name: 'Premium Collection', 
      tokens: 50,
      popular: true,
      items: '50 Sets',
      descriptionKey: 'packages.oneTime.premium.desc',
      featuresKey: 'packages.oneTime.premium.features'
    },
    { 
      id: 'ultimate_bundle', 
      name: 'Ultimate Bundle', 
      tokens: 80,
      items: t('packages.oneTime.ultimate.items') || 'ALL Content',
      descriptionKey: 'packages.oneTime.ultimate.desc',
      featuresKey: 'packages.oneTime.ultimate.features'
    }
  ];

  const specials = [
    { 
      id: 'valentines_special', 
      name: "Valentine's Special", 
      tokens: 25,
      badge: '💝 LIMITED',
      descriptionKey: 'packages.specials.valentines.desc',
      featuresKey: 'packages.specials.valentines.features'
    },
    { 
      id: 'summer_edition', 
      name: 'Summer Edition', 
      tokens: 30,
      badge: '☀️ SEASONAL',
      descriptionKey: 'packages.specials.summer.desc',
      featuresKey: 'packages.specials.summer.features'
    },
    { 
      id: 'birthday_bundle', 
      name: 'Birthday Bundle', 
      tokens: 40,
      badge: '🎂 EXCLUSIVE',
      descriptionKey: 'packages.specials.birthday.desc',
      featuresKey: 'packages.specials.birthday.features'
    }
  ];

  const ppvContent = [
    { 
      id: 'ppv_exclusive_1', 
      name: 'Private Session Vol.1', 
      tokens: 15,
      duration: '12 min',
      descriptionKey: 'packages.ppv.vol1.desc'
    },
    { 
      id: 'ppv_exclusive_2', 
      name: 'Fantasy Roleplay', 
      tokens: 20,
      duration: '18 min',
      descriptionKey: 'packages.ppv.vol2.desc'
    },
    { 
      id: 'ppv_exclusive_3', 
      name: 'VIP Experience', 
      tokens: 35,
      duration: '30 min',
      descriptionKey: 'packages.ppv.vol3.desc'
    }
  ];

  const handlePurchase = (item) => {
    if (!user) {
      toast.error(t('tokens.loginRequired') || 'Please login first');
      navigate('/login');
      return;
    }
    setConfirmDialog(item);
  };

  const confirmPurchase = async (finalPrice) => {
    if (!confirmDialog) return;
    
    const item = confirmDialog;
    const priceToCheck = finalPrice ?? item.tokens ?? item.price;
    
    if (creditBalance < priceToCheck) {
      toast.error(t('tokens.insufficientTokensDetail', { have: creditBalance, need: priceToCheck }) || `Not enough tokens! You have ${creditBalance} tokens, this costs ${priceToCheck} tokens.`);
      setConfirmDialog(null);
      navigate('/credits');
      return;
    }
    
    setPurchasing(item.id);
    setConfirmDialog(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/credits/spend`, {
        product_id: item.id,
        credits_amount: item.tokens ?? item.price
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCreditBalance(response.data.new_balance);
        const savedMsg = response.data.discount_amount > 0 
          ? ` (Je bespaarde ${response.data.discount_amount} tokens!)` 
          : '';
        toast.success(`🎉 ${item.name} ${t('tokens.purchaseSuccess', 'purchased')}!${savedMsg}`);
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || t('tokens.purchaseFailed') || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const getFeatures = (featuresKey, fallback) => {
    const features = t(featuresKey, { returnObjects: true });
    return Array.isArray(features) ? features : fallback || [];
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-16 px-6 lg:px-20">
        {/* Token Balance Bar */}
        <div className="flex justify-between items-center mb-8 p-4 glass rounded-xl border border-white/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <Coins className="w-6 h-6 text-black" />
            </div>
            <div>
              <p className="text-sm text-gray-400">{t('tokens.yourBalance')}</p>
              <p className="text-2xl font-bold text-[#D4AF37]">{creditBalance}</p>
            </div>
          </div>
          <Button onClick={() => navigate('/credits')} className="bg-[#D4AF37] hover:bg-[#B8963A] text-black">
            <Plus className="w-4 h-4 mr-2" />
            {t('tokens.buyTokens')}
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Crown className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-mono text-xs tracking-[0.3em] text-[#D4AF37] uppercase">{t('packages.badge')}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-4">
            {t('packages.title')} <span className="brand-gold text-5xl sm:text-6xl lg:text-7xl">{t('packages.titleAccent')}</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            {t('packages.subtitle')}
          </p>
        </div>

        {/* ✨ GOLD REWARDS UNLOCK SECTION ✨ */}
        <div className="mb-12">
          <div className={`relative overflow-hidden rounded-2xl border-2 ${isGoldUnlocked ? 'border-[#D4AF37] bg-gradient-to-r from-[#D4AF37]/20 via-[#0A0A0A] to-[#D4AF37]/20' : 'border-white/20 bg-gradient-to-r from-white/5 via-[#0A0A0A] to-white/5'}`}>
            {/* Animated glow effect */}
            {isGoldUnlocked && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4AF37]/10 to-transparent animate-pulse" />
            )}
            
            <div className="relative p-6 md:p-8">
              <div className="flex flex-col lg:flex-row items-center gap-6">
                {/* Left: Icon & Title */}
                <div className="flex items-center gap-4">
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${isGoldUnlocked ? 'bg-gradient-to-br from-[#D4AF37] to-[#B8963A] shadow-lg shadow-[#D4AF37]/30' : 'bg-white/10'}`}>
                    {isGoldUnlocked ? (
                      <MessageCircle className="w-10 h-10 text-black" />
                    ) : (
                      <Lock className="w-10 h-10 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${isGoldUnlocked ? 'bg-[#D4AF37] text-black' : 'bg-white/20 text-gray-300'}`}>
                        {isGoldUnlocked ? '✓ UNLOCKED' : 'EXCLUSIVE'}
                      </span>
                      <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold">
                      {isGoldUnlocked ? (
                        <span className="text-[#D4AF37]">Private Chat with Ella</span>
                      ) : (
                        <>Unlock <span className="text-[#D4AF37]">Private Chat</span></>
                      )}
                    </h2>
                    <p className="text-gray-400 mt-1">
                      {isGoldUnlocked 
                        ? "You have VIP access to chat privately with Ella! 💋" 
                        : "Spend €30 to unlock exclusive private conversations with Ella"}
                    </p>
                  </div>
                </div>

                {/* Right: Progress or CTA */}
                <div className="flex-1 w-full lg:w-auto">
                  {!isGoldUnlocked ? (
                    <div className="space-y-3">
                      {/* Progress bar */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-400">Your progress</span>
                        <span className="text-[#D4AF37] font-bold">€{totalSpent.toFixed(2)} / €{GOLD_UNLOCK_THRESHOLD}</span>
                      </div>
                      <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] rounded-full transition-all duration-1000 ease-out relative"
                          style={{ width: `${goldProgress}%` }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                        </div>
                      </div>
                      <p className="text-center text-sm text-gray-500">
                        {totalSpent > 0 
                          ? `Only €${(GOLD_UNLOCK_THRESHOLD - totalSpent).toFixed(2)} more to unlock!`
                          : "Make your first purchase to start unlocking rewards"}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div className="flex items-center gap-2 text-[#D4AF37]">
                        <Heart className="w-5 h-5 fill-current" />
                        <span className="font-medium">Ella is waiting for you...</span>
                      </div>
                      <Button 
                        onClick={() => navigate('/chat')}
                        className="bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] hover:from-[#B8963A] hover:to-[#D4AF37] text-black font-bold px-8 shadow-lg shadow-[#D4AF37]/30"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Start Private Chat
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Features list */}
              <div className={`mt-6 pt-6 border-t ${isGoldUnlocked ? 'border-[#D4AF37]/30' : 'border-white/10'}`}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { icon: MessageCircle, text: "Private 1-on-1 Chat", unlocked: isGoldUnlocked },
                    { icon: Heart, text: "Flirty AI Conversations", unlocked: isGoldUnlocked },
                    { icon: Sparkles, text: "Exclusive Photos", unlocked: isGoldUnlocked },
                    { icon: Zap, text: "Instant Responses", unlocked: isGoldUnlocked }
                  ].map((feature, idx) => (
                    <div key={idx} className={`flex items-center gap-2 ${feature.unlocked ? 'text-[#D4AF37]' : 'text-gray-500'}`}>
                      {feature.unlocked ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Lock className="w-4 h-4" />
                      )}
                      <span className="text-sm">{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-center bg-white/5 border border-white/10 mb-8 flex-wrap h-auto gap-2 p-2">
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Clock className="w-4 h-4 mr-2" />
              {t('packages.tabs.subscriptions')}
            </TabsTrigger>
            <TabsTrigger value="one-time" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <FileText className="w-4 h-4 mr-2" />
              {t('packages.tabs.oneTime')}
            </TabsTrigger>
            <TabsTrigger value="specials" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Gift className="w-4 h-4 mr-2" />
              {t('packages.tabs.specials')}
            </TabsTrigger>
            <TabsTrigger value="ppv" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Star className="w-4 h-4 mr-2" />
              PPV
            </TabsTrigger>
          </TabsList>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {subscriptions.map((sub) => {
                const features = getFeatures(sub.featuresKey, ['HD Quality', 'Archive access']);
                return (
                  <div 
                    key={sub.id}
                    data-testid={`subscription-${sub.id}`}
                    className={`relative p-6 glass rounded-2xl border transition-all duration-300 hover:-translate-y-2 ${
                      sub.popular 
                        ? 'border-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.2)]' 
                        : 'border-white/10 hover:border-[#D4AF37]/50'
                    }`}
                  >
                    {sub.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-black text-xs font-bold rounded-full">
                        {t('packages.popular')}
                      </div>
                    )}
                    <div className="text-4xl mb-4">{sub.icon}</div>
                    <h3 className="text-xl font-semibold mb-2">{t(sub.nameKey) || sub.name}</h3>
                    <div className="flex items-baseline gap-1 mb-2">
                      <Coins className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-3xl font-bold text-[#D4AF37]">{sub.tokens}</span>
                      <span className="text-gray-400">{t('tokens.tokenUnit')}</span>
                    </div>
                    <p className="text-sm text-gray-400 mb-4">{t(sub.descriptionKey) || ''}</p>
                    <ul className="space-y-2 mb-6">
                      {features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                          <Check className="w-4 h-4 text-[#D4AF37]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => handlePurchase(sub)}
                      disabled={purchasing === sub.id}
                      className="w-full py-5 bg-[#D4AF37] hover:bg-[#B8963A] text-black font-semibold"
                      data-testid={`buy-${sub.id}`}
                    >
                      <Coins className="w-4 h-4 mr-2" />
                      {purchasing === sub.id ? t('common.loading') : t('packages.buyNow')}
                    </Button>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* One-Time Tab */}
          <TabsContent value="one-time">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {oneTimePackages.map((pkg) => {
                const features = getFeatures(pkg.featuresKey, ['Instant download', 'Lifetime access']);
                return (
                  <div 
                    key={pkg.id}
                    data-testid={`package-${pkg.id}`}
                    className={`relative p-6 glass rounded-2xl border transition-all duration-300 hover:-translate-y-2 ${
                      pkg.popular 
                        ? 'border-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.2)]' 
                        : 'border-white/10 hover:border-[#D4AF37]/50'
                    }`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-black text-xs font-bold rounded-full">
                        {t('packages.bestValue')}
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                      <FileText className="w-8 h-8 text-[#D4AF37]" />
                      <span className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full text-sm font-medium">{pkg.items}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{pkg.name}</h3>
                    <div className="flex items-baseline gap-1 mb-2">
                      <Coins className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-3xl font-bold text-[#D4AF37]">{pkg.tokens}</span>
                      <span className="text-gray-400">{t('tokens.tokenUnit')}</span>
                    </div>
                    <p className="text-sm text-gray-400 mb-4">{t(pkg.descriptionKey) || ''}</p>
                    <ul className="space-y-2 mb-6">
                      {features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                          <Check className="w-4 h-4 text-[#D4AF37]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => handlePurchase(pkg)}
                      disabled={purchasing === pkg.id}
                      className="w-full py-5 bg-[#D4AF37] hover:bg-[#B8963A] text-black font-semibold"
                      data-testid={`buy-${pkg.id}`}
                    >
                      <Coins className="w-4 h-4 mr-2" />
                      {purchasing === pkg.id ? t('common.loading') : t('packages.buyNow')}
                    </Button>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Specials Tab */}
          <TabsContent value="specials">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {specials.map((special) => {
                const features = getFeatures(special.featuresKey, ['Exclusive content']);
                return (
                  <div 
                    key={special.id}
                    data-testid={`special-${special.id}`}
                    className="relative p-6 glass rounded-2xl border border-white/10 transition-all duration-300 hover:-translate-y-2 hover:border-[#D4AF37]/50"
                  >
                    <span className="absolute top-4 right-4 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-bold">
                      {special.badge}
                    </span>
                    <Gift className="w-8 h-8 text-[#D4AF37] mb-4" />
                    <h3 className="text-xl font-semibold mb-2">{special.name}</h3>
                    <div className="flex items-baseline gap-1 mb-2">
                      <Coins className="w-5 h-5 text-[#D4AF37]" />
                      <span className="text-3xl font-bold text-[#D4AF37]">{special.tokens}</span>
                      <span className="text-gray-400">{t('tokens.tokenUnit')}</span>
                    </div>
                    <p className="text-sm text-gray-400 mb-4">{t(special.descriptionKey) || ''}</p>
                    <ul className="space-y-2 mb-6">
                      {features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                          <Check className="w-4 h-4 text-[#D4AF37]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => handlePurchase(special)}
                      disabled={purchasing === special.id}
                      className="w-full py-5 bg-[#D4AF37] hover:bg-[#B8963A] text-black font-semibold"
                      data-testid={`buy-${special.id}`}
                    >
                      <Coins className="w-4 h-4 mr-2" />
                      {purchasing === special.id ? t('common.loading') : t('packages.buyNow')}
                    </Button>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* PPV Tab */}
          <TabsContent value="ppv">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {ppvContent.map((ppv) => (
                <div 
                  key={ppv.id}
                  data-testid={`ppv-${ppv.id}`}
                  className="relative p-6 glass rounded-2xl border border-white/10 transition-all duration-300 hover:-translate-y-2 hover:border-[#D4AF37]/50"
                >
                  <div className="flex items-center justify-between mb-4">
                    <Star className="w-8 h-8 text-[#D4AF37]" />
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs font-medium">
                      {ppv.duration}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{ppv.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <Coins className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-3xl font-bold text-[#D4AF37]">{ppv.tokens}</span>
                    <span className="text-gray-400">{t('tokens.tokenUnit')}</span>
                  </div>
                  <p className="text-sm text-gray-400 mb-6">{t(ppv.descriptionKey) || ''}</p>
                  <Button 
                    onClick={() => handlePurchase(ppv)}
                    disabled={purchasing === ppv.id}
                    className="w-full py-5 bg-[#D4AF37] hover:bg-[#B8963A] text-black font-semibold"
                    data-testid={`buy-${ppv.id}`}
                  >
                    <Coins className="w-4 h-4 mr-2" />
                    {purchasing === ppv.id ? t('common.loading') : t('packages.unlockNow')}
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Info Banner */}
        <div className="mt-16 p-8 glass rounded-2xl text-center bg-gradient-to-r from-[#D4AF37]/10 via-transparent to-[#D4AF37]/10">
          <Coins className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
          <h3 className="text-2xl font-medium mb-3">
            {t('packages.tokenBanner.title')} <span className="text-[#D4AF37]">Ella Gold Tokens</span>
          </h3>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto">
            {t('packages.tokenBanner.description')}
          </p>
          <Button 
            onClick={() => navigate('/credits')}
            className="bg-[#D4AF37] hover:bg-[#B8963A] text-black px-10 py-6 text-lg"
          >
            {t('tokens.buyTokens')}
          </Button>
        </div>
      </div>

      {/* Confirm Purchase Dialog */}
      <PurchaseDialog 
        product={confirmDialog ? {
          ...confirmDialog,
          price: confirmDialog.tokens ?? confirmDialog.price,
          description: t(confirmDialog.descriptionKey) || confirmDialog.description || ''
        } : null}
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onConfirm={confirmPurchase}
        creditBalance={creditBalance}
        purchasing={purchasing === confirmDialog?.id}
        t={t}
      />
    </div>
  );
}
